import {
  ACTIVITY_TYPES,
  CONDITION_OPERATORS,
  WORKFLOW_TRIGGER_TYPES,
  type ConditionOperator,
  type CrmModule,
  type WorkflowTriggerType,
} from '../enums.js';

/**
 * The vocabulary the workflow builder offers.
 *
 * Kept here, beside the evaluator, so the builder can never present a field or
 * an operator the engine will not understand. The API serves this list from
 * `/workflows/metadata` for the same reason.
 */

export const CONDITION_OPERATOR_LABELS: Record<ConditionOperator, string> = {
  EQUALS: 'is',
  NOT_EQUALS: 'is not',
  CONTAINS: 'contains',
  NOT_CONTAINS: 'does not contain',
  STARTS_WITH: 'starts with',
  ENDS_WITH: 'ends with',
  GREATER_THAN: 'is greater than',
  GREATER_OR_EQUAL: 'is at least',
  LESS_THAN: 'is less than',
  LESS_OR_EQUAL: 'is at most',
  IS_EMPTY: 'is empty',
  IS_NOT_EMPTY: 'is not empty',
  IN: 'is one of',
  NOT_IN: 'is none of',
  CHANGED: 'changed',
  BETWEEN: 'is between',
  RELATIVE_DATE_RANGE: 'is in',
};

/** Operators that take no value — the builder hides the value input for these. */
export const VALUELESS_OPERATORS = new Set<ConditionOperator>([
  'IS_EMPTY',
  'IS_NOT_EMPTY',
  'CHANGED',
]);

/** Operators that take a list rather than a single value. */
export const LIST_OPERATORS = new Set<ConditionOperator>(['IN', 'NOT_IN']);

/** A literal `[from, to]` pair — the builder offers two date inputs for these. */
export const RANGE_OPERATORS = new Set<ConditionOperator>(['BETWEEN']);

/** A named relative window (e.g. "this quarter") — the builder offers a preset picker. */
export const RELATIVE_RANGE_OPERATORS = new Set<ConditionOperator>(['RELATIVE_DATE_RANGE']);

export const WORKFLOW_TRIGGER_LABELS: Record<WorkflowTriggerType, string> = {
  RECORD_CREATED: 'When a record is created',
  RECORD_UPDATED: 'When a record is updated',
  FIELD_CHANGED: 'When specific fields change',
  RECORD_DELETED: 'When a record is deleted',
  SCHEDULED: 'On a schedule',
};

export interface WorkflowField {
  name: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'boolean' | 'choice';
  /** Present for `choice`; the builder turns these into a dropdown. */
  choices?: readonly string[];
  /** False for computed fields a workflow may read but not write. */
  writable?: boolean;
}

const OWNERSHIP: readonly WorkflowField[] = [
  { name: 'businessManagerId', label: 'Business manager', type: 'text', writable: false },
];

/**
 * Fields a workflow may address, per module.
 *
 * Deliberately a curated list rather than every column: `id`, `organizationId`
 * and the conversion bookkeeping are not things a rule should reason about,
 * and offering them invites rules that break when they fire.
 */
export const MODULE_FIELDS: Record<CrmModule, readonly WorkflowField[]> = {
  LEAD: [
    { name: 'firstName', label: 'First name', type: 'text' },
    { name: 'lastName', label: 'Last name', type: 'text' },
    { name: 'businessName', label: 'Business name', type: 'text' },
    { name: 'title', label: 'Job title', type: 'text' },
    { name: 'email', label: 'Email', type: 'text' },
    { name: 'phone', label: 'Phone', type: 'text' },
    { name: 'website', label: 'Website', type: 'text' },
    { name: 'businessCategory', label: 'Business category', type: 'choice' },
    { name: 'country', label: 'Country', type: 'text' },
    { name: 'city', label: 'City', type: 'text' },
    { name: 'status', label: 'Status', type: 'choice' },
    { name: 'source', label: 'Source', type: 'choice' },
    { name: 'description', label: 'Description', type: 'text' },
    ...OWNERSHIP,
  ],
  ACCOUNT: [
    { name: 'name', label: 'Name', type: 'text' },
    { name: 'businessCategory', label: 'Business category', type: 'text' },
    { name: 'website', label: 'Website', type: 'text' },
    { name: 'phone', label: 'Phone', type: 'text' },
    { name: 'billingCountry', label: 'Country', type: 'text' },
    { name: 'billingCity', label: 'City', type: 'text' },
    { name: 'description', label: 'Description', type: 'text' },
    ...OWNERSHIP,
  ],
  CONTACT: [
    { name: 'firstName', label: 'First name', type: 'text' },
    { name: 'lastName', label: 'Last name', type: 'text' },
    { name: 'email', label: 'Email', type: 'text' },
    { name: 'phone', label: 'Phone', type: 'text' },
    { name: 'title', label: 'Job title', type: 'text' },
    { name: 'department', label: 'Department', type: 'text' },
    { name: 'emailOptOut', label: 'Email opt-out', type: 'boolean' },
    { name: 'mailingCountry', label: 'Country', type: 'text' },
    { name: 'description', label: 'Description', type: 'text' },
    ...OWNERSHIP,
  ],
  DEAL: [
    { name: 'name', label: 'Name', type: 'text' },
    { name: 'amount', label: 'Amount', type: 'number' },
    { name: 'probability', label: 'Probability', type: 'number' },
    { name: 'status', label: 'Status', type: 'choice', choices: ['OPEN', 'WON', 'LOST'] },
    { name: 'stageId', label: 'Stage', type: 'text' },
    { name: 'expectedCloseDate', label: 'Expected close date', type: 'date' },
    { name: 'nextStep', label: 'Next step', type: 'text' },
    { name: 'lostReason', label: 'Lost reason', type: 'text' },
    { name: 'description', label: 'Description', type: 'text' },
    ...OWNERSHIP,
  ],
  ACTIVITY: [
    { name: 'subject', label: 'Subject', type: 'text' },
    { name: 'type', label: 'Type', type: 'choice', choices: [...ACTIVITY_TYPES] },
    {
      name: 'status',
      label: 'Status',
      type: 'choice',
      choices: ['NOT_STARTED', 'IN_PROGRESS', 'COMPLETED', 'DEFERRED', 'CANCELLED'],
    },
    { name: 'priority', label: 'Priority', type: 'choice', choices: ['LOW', 'NORMAL', 'HIGH'] },
    { name: 'dueAt', label: 'Due', type: 'date' },
    { name: 'outcome', label: 'Outcome', type: 'text' },
    ...OWNERSHIP,
  ],
  // No automation surface yet; listed so the map is total over CrmModule and
  // the builder degrades to "no fields" rather than crashing.
  // Sales documents and catalogue records are not automated yet. Empty rather
  // than absent: the map is exhaustive over CrmModule, so adding a module to
  // the enum forces a decision here rather than silently omitting it.
  PRODUCT: [],
  QUOTE: [],
  VENDOR: [],
  SOLUTION: [],
  SALES_ORDER: [],

  CAMPAIGN: [
    { name: 'name', label: 'Campaign name', type: 'text' },
    { name: 'type', label: 'Type', type: 'choice' },
    { name: 'status', label: 'Status', type: 'choice' },
    { name: 'startDate', label: 'Start date', type: 'date' },
    { name: 'endDate', label: 'End date', type: 'date' },
    ...OWNERSHIP,
  ],

  // Cases are the module people actually automate: escalate on priority,
  // notify on a status change, assign by product.
  CASE: [
    { name: 'subject', label: 'Subject', type: 'text' },
    { name: 'status', label: 'Status', type: 'choice' },
    { name: 'priority', label: 'Priority', type: 'choice' },
    { name: 'origin', label: 'Origin', type: 'choice' },
    { name: 'reason', label: 'Reason', type: 'text' },
    { name: 'type', label: 'Type', type: 'text' },
    ...OWNERSHIP,
  ],

  INVOICE: [
    { name: 'subject', label: 'Subject', type: 'text' },
    { name: 'status', label: 'Status', type: 'choice' },
    { name: 'invoiceDate', label: 'Invoice date', type: 'date' },
    { name: 'dueDate', label: 'Due date', type: 'date' },
    { name: 'total', label: 'Total', type: 'number', writable: false },
    ...OWNERSHIP,
  ],
  PAYMENT_RECEIVED: [
    { name: 'amount', label: 'Amount', type: 'number', writable: false },
    { name: 'receivedOn', label: 'Received on', type: 'date' },
    { name: 'method', label: 'Method', type: 'choice' },
    { name: 'reference', label: 'Reference', type: 'text' },
    { name: 'notes', label: 'Notes', type: 'text' },
    ...OWNERSHIP,
  ],

  EXPENSE: [
    { name: 'name', label: 'Expense', type: 'text', writable: false },
    { name: 'expenseDate', label: 'Date', type: 'date' },
    { name: 'amount', label: 'Amount', type: 'number', writable: false },
    { name: 'category', label: 'Category', type: 'choice' },
    { name: 'paidFromAccount', label: 'Paid from account', type: 'choice' },
    { name: 'vendor', label: 'Vendor/Merchant', type: 'text' },
    ...OWNERSHIP,
  ],
};

/** Fields a workflow's UPDATE_FIELD action may write. */
export function writableFields(module: CrmModule): readonly WorkflowField[] {
  return MODULE_FIELDS[module].filter((field) => field.writable !== false);
}

export const ALL_OPERATORS = CONDITION_OPERATORS;
export const ALL_TRIGGERS = WORKFLOW_TRIGGER_TYPES;
